import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
} from '@angular/core'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatSnackBar } from '@angular/material/snack-bar'
import { ActivatedRoute, Router } from '@angular/router'

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import * as Highcharts from 'highcharts'
import 'highcharts/modules/accessibility'
import { HighchartsChartModule } from 'highcharts-angular'
import {
  catchError,
  distinctUntilChanged,
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
  take,
  throwError,
} from 'rxjs'

import { TagMatchMode } from '../../../../_generated/graphql'
import {
  Item,
  MultipleSelectComponent,
} from '../../../components/multiple-select/multiple-select.component'
import { AssessmentStore } from '../../../stores/assessment.store'
import { hasManagementRole } from '../../../guards/kpi-dashboard-access'
import { AuthService } from '../../../services/auth.service'
import {
  KPI_DASHBOARD_MEMBER_NOT_FOUND_MESSAGE,
  KpiDashboardDefinition,
  KpiDashboardService,
  KpiOperatorSummary,
} from '../../../services/kpi-dashboard/kpi-dashboard.service'
import { UserInfo } from '../../../services/user/user.service'
import { UserStore } from '../../../stores/user.store'
import { captureException } from '../../../util/captureException'
import { handleDashboardNotFound } from '../handle-dashboard-not-found'
import {
  isKpiCustomError,
  CustomErrorCode,
} from '../../../util/throwIfCustomError'
import {
  KpiFeedbackSheetSectionComponent,
  KpiFeedbackSheetViewer,
} from '../feedback-sheet-section/kpi-feedback-sheet-section.component'
import { KeyActionSelectorComponent } from '../key-action-selector/key-action-selector.component'
import {
  KeyActionViewModel,
  OPERATOR_SCORE_COUNT,
  buildKeyActionViewModels,
  haveSameIds,
  selectedIdsFromKeyActions,
  withSelectedKeyActionIds,
} from '../key-action-selector/key-action-selector.logic'
import {
  KeyActionTrendViewModel,
  KeyActionDailyTrendViewModel,
  buildKeyActionDailyTrendViewModel,
  shiftPeriod,
  OperatorMetricViewModel,
  TrendChartViewModel,
  buildKeyActionTrendViewModel,
  buildOperatorMetricViewModels,
  buildTrendChartViewModels,
  createAvatarText,
  createTrendRangeText,
  formatPeriod,
} from './operator-detail.logic'

type OperatorDetailTab = 'summary' | 'feedback'

interface DailyTrendState {
  chart: KeyActionDailyTrendViewModel | null
  errorMessage: string
}

interface OperatorDetailViewModel {
  dashboard: KpiDashboardDefinition
  summary: KpiOperatorSummary
  user: UserInfo
  metricCards: OperatorMetricViewModel[]
  keyActions: KeyActionViewModel[]
  trendCharts: TrendChartViewModel[]
  keyActionTrendChart: KeyActionTrendViewModel
  trendRangeText: string | null
  latestPeriodText: string | null
  avatarText: string
  canManage: boolean
  feedbackSheetViewer: KpiFeedbackSheetViewer
  savedKeyActionIds: string[]
  hasKeyActionChanges: boolean
}

/** 保存成功後のサマリー再取得失敗を、保存自体の失敗と区別する。 */
class SummaryReloadError extends Error {}

@UntilDestroy()
@Component({
  selector: 'app-kpi-operator-detail',
  standalone: true,
  imports: [
    HighchartsChartModule,
    MultipleSelectComponent,
    KeyActionSelectorComponent,
    KpiFeedbackSheetSectionComponent,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './kpi-operator-detail.component.html',
  styleUrl: './kpi-operator-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KpiOperatorDetailComponent implements OnInit {
  readonly Highcharts = Highcharts
  readonly operatorScoreCount = OPERATOR_SCORE_COUNT
  tagItems: Item[] = []
  readonly tagMatchOptions = [
    { value: TagMatchMode.Any, label: 'いずれかを含む' },
    { value: TagMatchMode.All, label: 'すべてを含む' },
  ] as const
  tagMatch = TagMatchMode.Any
  isKeyActionScoresLoading = false
  keyActionScoresErrorMessage = ''
  private keyActionScoresRequestId = 0

  dashboard: KpiDashboardDefinition | null = null
  summary: KpiOperatorSummary | null = null
  metricCards: OperatorMetricViewModel[] = []
  keyActions: KeyActionViewModel[] = []
  trendCharts: TrendChartViewModel[] = []
  keyActionTrendChart: KeyActionTrendViewModel | null = null
  keyActionDailyTrend: KeyActionDailyTrendViewModel | null = null
  dailyTrendPeriod: string | null = null
  isDailyTrendLoading = false
  dailyTrendErrorMessage = ''
  private dailyTrendRequestId = 0
  trendRangeText: string | null = null
  latestPeriodText: string | null = null
  avatarText = ''
  canManage = false
  isLoading = true
  loadErrorMessage = ''
  saveErrorMessage = ''
  keyActionTrendReloadErrorMessage = ''
  isSaving = false
  hasKeyActionChanges = false
  feedbackSheetViewer: KpiFeedbackSheetViewer | null = null
  activeTab: OperatorDetailTab = 'summary'

  @ViewChild('detailContent') private detailContent?: ElementRef<HTMLElement>

  dashboardId = ''
  operatorUserId = ''
  private savedKeyActionIds: string[] = []
  private user: UserInfo | null = null

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private userStore: UserStore,
    private assessmentStore: AssessmentStore,
    private kpiDashboardService: KpiDashboardService,
    private snackBar: MatSnackBar,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  /** ルート ID を検証し、オペレーターの KPI サマリーを初期化する。 */
  ngOnInit(): void {
    const dashboardId = this.route.snapshot.paramMap.get('dashboardId')
    const userId = this.route.snapshot.paramMap.get('userId')
    if (
      dashboardId == null ||
      dashboardId === '' ||
      userId == null ||
      userId === ''
    ) {
      this.isLoading = false
      this.loadErrorMessage =
        'オペレーター情報を表示するための ID を取得できませんでした。'
      captureException(new Error('dashboardId or userId is missing'))
      return
    }

    this.dashboardId = dashboardId
    this.operatorUserId = userId
    this.route.queryParamMap
      .pipe(
        map(
          (params): OperatorDetailTab =>
            params.get('tab') === 'feedback' ? 'feedback' : 'summary',
        ),
        distinctUntilChanged(),
        untilDestroyed(this),
      )
      .subscribe((tab) => {
        this.activeTab = tab
        this.changeDetectorRef.markForCheck()
      })
    this.assessmentStore
      .getAssessmentTagMasters()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (tags) => {
          this.tagItems = tags.map((tag) => ({
            id: tag.id,
            label: tag.name,
            checked: false,
          }))
          this.changeDetectorRef.markForCheck()
        },
        error: (error: unknown) => {
          captureException(error)
          this.tagItems = []
          this.changeDetectorRef.markForCheck()
        },
      })
    this.load()
  }

  /** タグの一致条件の変更後、multiple-select が選択内容を通知して再取得する。 */
  changeTagMatch(mode: string): void {
    if (!Object.values(TagMatchMode).includes(mode as TagMatchMode)) {
      throw new Error('タグの一致条件が不正です')
    }
    this.tagMatch = mode as TagMatchMode
  }

  changeSelectedTags(items: Item[]): void {
    this.tagItems = items
    this.reloadKeyActionScores()
  }

  private selectedTagIds(): string[] {
    return this.tagItems
      .filter((item) => item.checked)
      .map((item) => {
        if (item.id == null) throw new Error('タグ ID がありません')
        return item.id
      })
  }

  /** 平均だけを差し替え、未保存の KA 選択と月次・日次の表示モデルを保持する。 */
  private reloadKeyActionScores(): void {
    const requestId = ++this.keyActionScoresRequestId
    this.isKeyActionScoresLoading = true
    this.keyActionScoresErrorMessage = ''
    this.changeDetectorRef.markForCheck()
    this.kpiDashboardService
      .operatorSummary(
        this.dashboardId,
        this.operatorUserId,
        OPERATOR_SCORE_COUNT,
        this.selectedTagIds(),
        this.tagMatch,
      )
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (summary) => {
          if (requestId !== this.keyActionScoresRequestId) return
          if (this.dashboard == null || this.summary == null) {
            throw new Error('KPI dashboard or summary is missing')
          }
          this.summary = {
            ...this.summary,
            keyActionScores: summary.keyActionScores,
          }
          this.keyActions = buildKeyActionViewModels(
            this.dashboard.keyActions,
            summary.keyActionScores,
            selectedIdsFromKeyActions(this.keyActions),
          )
          this.isKeyActionScoresLoading = false
          this.changeDetectorRef.markForCheck()
        },
        error: (error: unknown) => {
          if (requestId !== this.keyActionScoresRequestId) return
          captureException(error)
          this.isKeyActionScoresLoading = false
          this.keyActionScoresErrorMessage =
            '平均スコアを取得できませんでした。表示中のスコアは前回のタグ条件での結果です。タグを選択し直してください。'
          this.changeDetectorRef.markForCheck()
        },
      })
  }

  /**
   * 選択したセクションを URL に反映し、遷移後はページ先頭へスクロールしてコンテンツへフォーカスを移す。
   * focus() 任せのスクロールは要素の一部が見えていると動かないため、scrollTo で明示する。
   * 選択中のタブなら何もしない。
   */
  selectTab(tab: OperatorDetailTab): void {
    if (tab === this.activeTab) {
      return
    }

    void this.router
      .navigate([], {
        relativeTo: this.route,
        queryParams: { tab: tab === 'summary' ? null : tab },
        queryParamsHandling: 'merge',
      })
      .then((navigated) => {
        if (navigated) {
          this.detailContent?.nativeElement.focus({ preventScroll: true })
          window.scrollTo({ top: 0 })
        }
      })
      .catch((error: unknown) => captureException(error))
  }

  retry(): void {
    this.load()
  }

  /** 詳細固有のタブを除き、一覧から引き継いだ URL 条件をそのまま返す。 */
  openOperatorList(): void {
    if (!this.canManage) {
      return
    }

    const params = this.route.snapshot.queryParamMap
    const queryParams = Object.fromEntries(
      params.keys
        .filter((key) => key !== 'tab')
        .map((key) => {
          const values = params.getAll(key)
          return [key, values.length === 1 ? values[0] : values]
        }),
    )
    void this.router.navigate(['/kpi-dashboards', this.dashboardId], {
      queryParams,
    })
  }

  openSettings(): void {
    if (!this.canManage) {
      return
    }

    void this.router.navigate(['/kpi-dashboards', this.dashboardId, 'settings'])
  }

  onKeyActionSelectionChanged(selectedIds: string[]): void {
    if (!this.canManage || this.isSaving) {
      return
    }

    this.keyActions = withSelectedKeyActionIds(this.keyActions, selectedIds)
    this.updateKeyActionChanges()
    this.saveErrorMessage = ''
  }

  saveKeyActions(): void {
    if (!this.canManage || this.isSaving || !this.hasKeyActionChanges) {
      return
    }

    const selectedIds = selectedIdsFromKeyActions(this.keyActions)
    let summaryRequestId: number
    this.isSaving = true
    this.saveErrorMessage = ''
    this.changeDetectorRef.markForCheck()
    this.kpiDashboardService
      .saveOperatorKeyActions(
        this.dashboardId,
        this.operatorUserId,
        selectedIds,
      )
      .pipe(
        switchMap((saved) => {
          if (!saved) {
            return throwError(
              () => new Error('saveKpiOperatorKeyActions returned false'),
            )
          }
          // 保存前の日次リクエストが保存後の選択状態を上書きしないよう無効化する。
          this.dailyTrendRequestId++
          summaryRequestId = ++this.keyActionScoresRequestId
          this.isKeyActionScoresLoading = false
          this.keyActionScoresErrorMessage = ''
          return this.kpiDashboardService
            .operatorSummary(
              this.dashboardId,
              this.operatorUserId,
              OPERATOR_SCORE_COUNT,
              this.selectedTagIds(),
              this.tagMatch,
            )
            .pipe(
              map((summary) => {
                if (this.dashboard == null || this.user == null) {
                  throw new Error('KPI dashboard or user is missing')
                }
                return buildDetailViewModel(this.dashboard, summary, this.user)
              }),
              catchError((error: unknown) =>
                throwError(
                  () =>
                    new SummaryReloadError('KPI summary reload failed', {
                      cause: error,
                    }),
                ),
              ),
              switchMap((viewModel) => {
                // 全解除後は対象月を捨て、次に設定した時は初期表示と同じ当月から始める
                if (viewModel.summary.selectedKeyActionIds.length === 0) {
                  this.dailyTrendPeriod = null
                  this.isDailyTrendLoading = false
                  this.changeDetectorRef.markForCheck()
                  return of({
                    viewModel,
                    dailyTrend: { chart: null, errorMessage: '' },
                  })
                }
                const { periodFrom, periodTo } =
                  viewModel.summary.keyActionTrendWindow
                const period =
                  this.dailyTrendPeriod == null
                    ? periodTo
                    : this.dailyTrendPeriod < periodFrom
                      ? periodFrom
                      : this.dailyTrendPeriod > periodTo
                        ? periodTo
                        : this.dailyTrendPeriod
                this.dailyTrendPeriod = period
                this.isDailyTrendLoading = true
                this.changeDetectorRef.markForCheck()
                return this.fetchDailyTrend(
                  viewModel.dashboard,
                  viewModel.summary,
                  period,
                ).pipe(map((dailyTrend) => ({ viewModel, dailyTrend })))
              }),
            )
        }),
        untilDestroyed(this),
      )
      .subscribe({
        next: ({ viewModel, dailyTrend }) => {
          // 保存後の再取得中にタグが変わった場合、古い条件の平均を反映しない。
          if (summaryRequestId !== this.keyActionScoresRequestId) {
            if (this.summary == null) throw new Error('KPI summary is missing')
            viewModel.summary = {
              ...viewModel.summary,
              keyActionScores: this.summary.keyActionScores,
            }
            viewModel.keyActions = buildKeyActionViewModels(
              viewModel.dashboard.keyActions,
              this.summary.keyActionScores,
              viewModel.savedKeyActionIds,
            )
          }
          this.applyViewModel(viewModel)
          this.applyDailyTrend(dailyTrend)
          this.isSaving = false
          this.snackBar.open('重点強化KeyActionを保存しました。', '閉じる', {
            duration: 3000,
          })
          this.changeDetectorRef.markForCheck()
        },
        error: (error: unknown) => this.handleSaveError(error, selectedIds),
      })
  }

  /** 対象月はサーバーの窓を基準にし、未取得時は月名を表示しない。 */
  get dailyTrendPeriodText(): string {
    return this.dailyTrendPeriod == null
      ? ''
      : formatPeriod(this.dailyTrendPeriod)
  }

  /** 失敗・空状態でも表示中の月から移動可否を判断する。 */
  get canGoDailyTrendPrevious(): boolean {
    return (
      this.canNavigateDailyTrend &&
      this.summary != null &&
      this.dailyTrendPeriod != null &&
      this.dailyTrendPeriod > this.summary.keyActionTrendWindow.periodFrom
    )
  }

  get canGoDailyTrendNext(): boolean {
    return (
      this.canNavigateDailyTrend &&
      this.summary != null &&
      this.dailyTrendPeriod != null &&
      this.dailyTrendPeriod < this.summary.keyActionTrendWindow.periodTo
    )
  }

  private get canNavigateDailyTrend(): boolean {
    return (
      !this.isDailyTrendLoading &&
      !this.isSaving &&
      this.keyActionTrendReloadErrorMessage === ''
    )
  }

  /** 集計窓内の前月を取得する。 */
  onDailyTrendPrevious(): void {
    if (!this.canGoDailyTrendPrevious || this.dailyTrendPeriod == null) return
    this.loadDailyTrend(shiftPeriod(this.dailyTrendPeriod, -1))
  }

  /** 集計窓内の翌月を取得する。 */
  onDailyTrendNext(): void {
    if (!this.canGoDailyTrendNext || this.dailyTrendPeriod == null) return
    this.loadDailyTrend(shiftPeriod(this.dailyTrendPeriod, 1))
  }

  /** 失敗した対象月の日次推移を再取得する。 */
  retryDailyTrend(): void {
    if (!this.canNavigateDailyTrend || this.dailyTrendPeriod == null) return
    this.loadDailyTrend(this.dailyTrendPeriod)
  }

  /** 日次の失敗をブロック内で扱い、サマリー表示・保存結果から切り離す。 */
  private fetchDailyTrend(
    dashboard: KpiDashboardDefinition,
    summary: KpiOperatorSummary,
    period: string,
  ): Observable<DailyTrendState> {
    return this.kpiDashboardService
      .operatorKeyActionDailyTrend(
        this.dashboardId,
        this.operatorUserId,
        period,
      )
      .pipe(
        map((result) => ({
          chart: buildKeyActionDailyTrendViewModel(
            dashboard.keyActions,
            summary.selectedKeyActionIds,
            result,
          ),
          errorMessage: '',
        })),
        catchError((error: unknown) => {
          captureException(error)
          return of({
            chart: null,
            errorMessage:
              '重点強化 KeyAction の日次推移を取得できませんでした。',
          })
        }),
      )
  }

  /** 対象月を更新し、最新の日次リクエストだけを表示に反映する。 */
  private loadDailyTrend(period: string): void {
    if (this.dashboard == null || this.summary == null) {
      throw new Error('KPI dashboard or summary is missing')
    }
    const requestId = ++this.dailyTrendRequestId
    this.dailyTrendPeriod = period
    this.isDailyTrendLoading = true
    this.dailyTrendErrorMessage = ''
    this.changeDetectorRef.markForCheck()
    this.fetchDailyTrend(this.dashboard, this.summary, period)
      .pipe(untilDestroyed(this))
      .subscribe((state) => {
        if (requestId !== this.dailyTrendRequestId) return
        this.applyDailyTrend(state)
        this.changeDetectorRef.markForCheck()
      })
  }

  /** 日次の表示モデルと取得状態を一括更新する。 */
  private applyDailyTrend(state: DailyTrendState): void {
    this.keyActionDailyTrend = state.chart
    this.dailyTrendErrorMessage = state.errorMessage
    this.isDailyTrendLoading = false
  }

  private load(): void {
    this.keyActionScoresRequestId++
    this.isKeyActionScoresLoading = false
    this.keyActionScoresErrorMessage = ''
    this.dailyTrendRequestId++
    this.keyActionDailyTrend = null
    this.dailyTrendPeriod = null
    this.dailyTrendErrorMessage = ''
    this.isDailyTrendLoading = false
    this.isLoading = true
    this.loadErrorMessage = ''
    this.saveErrorMessage = ''
    this.keyActionTrendReloadErrorMessage = ''
    forkJoin({
      dashboard: this.kpiDashboardService.getDashboard(this.dashboardId),
      summary: this.kpiDashboardService.operatorSummary(
        this.dashboardId,
        this.operatorUserId,
        OPERATOR_SCORE_COUNT,
        this.selectedTagIds(),
        this.tagMatch,
      ),
      user: this.authService.user$.pipe(
        take(1),
        switchMap((authUser) => {
          if (authUser == null) {
            return throwError(() => new Error('Authenticated user is missing'))
          }
          return this.userStore.getOneSelf({ useCache: true })
        }),
      ),
    })
      .pipe(
        map(({ dashboard, summary, user }) =>
          buildDetailViewModel(dashboard, summary, user),
        ),
        untilDestroyed(this),
      )
      .subscribe({
        next: (viewModel) => {
          this.applyViewModel(viewModel)
          this.isLoading = false
          if (viewModel.summary.selectedKeyActionIds.length > 0) {
            this.loadDailyTrend(viewModel.summary.keyActionTrendWindow.periodTo)
          }
          this.changeDetectorRef.markForCheck()
        },
        error: (error: unknown) => {
          if (handleDashboardNotFound(error, this.router, this.snackBar)) {
            this.isLoading = false
            this.changeDetectorRef.markForCheck()
            return
          }
          if (
            !isKpiCustomError(error, CustomErrorCode.NOT_FOUND, [
              KPI_DASHBOARD_MEMBER_NOT_FOUND_MESSAGE,
            ])
          )
            captureException(error)
          this.isLoading = false
          this.loadErrorMessage =
            'オペレーター個人支援ボードの読み込みに失敗しました。'
          this.changeDetectorRef.markForCheck()
        },
      })
  }

  /** サーバーから構築した表示モデルを一括適用し、保存済みの選択状態を同期する。 */
  private applyViewModel(viewModel: OperatorDetailViewModel): void {
    this.user = viewModel.user
    this.keyActionTrendReloadErrorMessage = ''
    this.dashboard = viewModel.dashboard
    this.summary = viewModel.summary
    this.metricCards = viewModel.metricCards
    this.keyActions = viewModel.keyActions
    this.trendCharts = viewModel.trendCharts
    this.keyActionTrendChart = viewModel.keyActionTrendChart
    this.trendRangeText = viewModel.trendRangeText
    this.latestPeriodText = viewModel.latestPeriodText
    this.avatarText = viewModel.avatarText
    this.canManage = viewModel.canManage
    this.feedbackSheetViewer = viewModel.feedbackSheetViewer
    this.savedKeyActionIds = viewModel.savedKeyActionIds
    this.hasKeyActionChanges = viewModel.hasKeyActionChanges
  }

  /** 保存結果に応じて、保存失敗と月次推移の再取得失敗を区別して通知する。 */
  private handleSaveError(error: unknown, selectedIds: string[]): void {
    captureException(error)
    this.isSaving = false
    this.changeDetectorRef.markForCheck()
    const reloadFailed = error instanceof SummaryReloadError
    if (this.summary == null || this.keyActionTrendChart == null) {
      throw new Error('KPI summary or KeyAction trend chart is missing')
    }
    if (reloadFailed) {
      this.savedKeyActionIds = [...selectedIds]
      this.updateKeyActionChanges()
      this.summary = { ...this.summary, selectedKeyActionIds: [...selectedIds] }
      this.keyActionTrendChart = { ...this.keyActionTrendChart, options: null }
      this.keyActionDailyTrend = null
      this.isDailyTrendLoading = false
      this.dailyTrendErrorMessage = ''
      this.keyActionTrendReloadErrorMessage =
        '保存内容を反映できませんでした。画面を再読み込みすると最新の推移を表示します。'
    }
    this.saveErrorMessage = reloadFailed
      ? '重点強化KeyActionは保存されています。最新の推移を取得できなかったため、画面を再読み込みしてください。'
      : '重点強化KeyActionを保存できませんでした。時間を置いて再度お試しください。'
  }

  private updateKeyActionChanges(): void {
    this.hasKeyActionChanges = !haveSameIds(
      selectedIdsFromKeyActions(this.keyActions),
      this.savedKeyActionIds,
    )
  }
}

/** サマリーから KPI と重点 KA の表示モデルを組み立てる。 */
export function buildDetailViewModel(
  dashboard: KpiDashboardDefinition,
  summary: KpiOperatorSummary,
  user: UserInfo,
): OperatorDetailViewModel {
  const monthlyTrend = [...summary.monthlyTrend].sort((left, right) =>
    left.period.localeCompare(right.period),
  )
  const metricCards =
    summary.latestPeriod == null
      ? []
      : buildOperatorMetricViewModels(dashboard.items, summary.metrics)
  const trendCharts =
    monthlyTrend.length === 0
      ? []
      : buildTrendChartViewModels(dashboard.items, monthlyTrend)
  const keyActions = buildKeyActionViewModels(
    dashboard.keyActions,
    summary.keyActionScores,
    summary.selectedKeyActionIds,
  )
  const savedKeyActionIds = [...summary.selectedKeyActionIds]
  const canManage = hasManagementRole(user)

  return {
    dashboard,
    summary: { ...summary, monthlyTrend },
    user,
    metricCards,
    keyActions,
    trendCharts,
    keyActionTrendChart: buildKeyActionTrendViewModel(
      dashboard.keyActions,
      summary.selectedKeyActionIds,
      summary.keyActionMonthlyTrend,
      summary.keyActionTrendWindow,
    ),
    trendRangeText: createTrendRangeText(monthlyTrend),
    latestPeriodText:
      summary.latestPeriod == null ? null : formatPeriod(summary.latestPeriod),
    avatarText: createAvatarText(summary.userName),
    canManage,
    feedbackSheetViewer: { userId: user.id, canManage },
    savedKeyActionIds,
    hasKeyActionChanges: !haveSameIds(
      selectedIdsFromKeyActions(keyActions),
      savedKeyActionIds,
    ),
  }
}
